<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';

import PickingStatusChip from '@/components/chips/PickingStatusChip.vue';
import DetailedJobInfoDescListItem from '@/components/items/DetailedJobInfoDescListItem.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import * as commonConstants from '@/constants/common';
import * as routeNames from '@/constants/routeNames';

export interface Props {
  job?: ReadonlyDeep<
    RequiredKeys<
      Partial<
        Pick<
          openapi.JobDetailedOpenapi,
          | 'booked_by'
          | 'booked_at'
          | 'completed_at'
          | 'cancelled_at'
          | 'caller'
          | 'delay'
          | 'is_pod_required'
          | 'phone'
          | 'ref1'
          | 'ref2'
          | 'ref3'
          | 'ref4'
          | 'wh_status'
          | 'invoice_status'
          | 'payment_status'
          | 'picking_status'
          | 'schedule_board'
          | 'rockhopper_date'
        >
      >
    >
  >;
  detailedCustomer?: ReadonlyDeep<
    RequiredKeys<
      Partial<
        Pick<
          openapi.CustomerDetailContentOpenapi,
          'ref1_prompt' | 'ref2_prompt' | 'ref3_prompt' | 'ref4_prompt'
        >
      >
    >
  >;
  loading?: boolean;
  isStaff?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  job: undefined,
  detailedCustomer: undefined,
  loading: false,
  isStaff: false,
});

const job = computed(() => props.job || {});

const jobDelay = computed(() => {
  if (!props.job?.delay) return;
  const numHours = Math.floor(props.job.delay[0] / 60 / 60);
  return `${numHours} ${numHours > 1 ? 'hrs' : 'hr'}`;
});

const timeZone = 'Australia/Sydney';

function formatDate(date: string, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d'); // 04:50 PM, Sep 24
}

// TODO Odoo checklist
const isOdoo = false;
</script>

<template>
  <dl
    :class="$style.detailedJobAdditionalInfoDescList"
    data-testid="detailedJobAdditionalInfoDescList"
  >
    <div :class="$style.wrapper">
      <div v-if="props.isStaff" :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Picking status">
          <template v-if="job.picking_status">
            <PickingStatusChip :status="job.picking_status" />
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Phone">
          <template v-if="job.phone && job.phone.length > 1">
            <a :href="`callto://${job.phone}`" :class="$style.a">
              <KParentTooltip>Call via Skype</KParentTooltip>
              <v-icon :class="$style.icon" icon="mdi-phone-outline"></v-icon>
              {{ job.phone }}
            </a>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Caller">
          <template v-if="job.caller">
            {{ job.caller }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Booked by">
          <template v-if="job.booked_by">
            {{ job.booked_by }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Booked at">
          <template v-if="job.booked_at">
            <KParentTooltip>{{ job.booked_at }}</KParentTooltip>
            {{ formatDate(job.booked_at, timeZone) }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Completed at">
          <template v-if="job.completed_at">
            <KParentTooltip>{{ job.completed_at }}</KParentTooltip>
            {{ formatDate(job.completed_at, timeZone) }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Cancelled at">
          <template v-if="job.cancelled_at">
            <KParentTooltip>{{ job.cancelled_at }}</KParentTooltip>
            {{ formatDate(job.cancelled_at, timeZone) }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem
          :class="$style.item"
          :term="props.detailedCustomer?.ref1_prompt || 'Reference 1'"
        >
          <template v-if="job.ref1">
            {{ job.ref1 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem
          :class="$style.item"
          :term="props.detailedCustomer?.ref2_prompt || 'Reference 2'"
        >
          <template v-if="job.ref2">
            {{ job.ref2 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem
          :class="$style.item"
          :term="props.detailedCustomer?.ref3_prompt || 'Reference 3'"
        >
          <template v-if="job.ref3">
            {{ job.ref3 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem
          :class="$style.item"
          :term="props.detailedCustomer?.ref4_prompt || 'Reference 4'"
        >
          <template v-if="job.ref4">
            {{ job.ref4 }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="POD required">
          <template v-if="props.loading">
            {{ commonConstants.EM_DASH }}
          </template>
          <template v-else-if="job.is_pod_required === true">Yes</template>
          <template v-else-if="job.is_pod_required === false">No</template>
          <template v-else>
            {{ commonConstants.EM_DASH }}
          </template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Delay">
          <template v-if="job.delay && job.delay[1] !== 0">
            {{ jobDelay }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Checklist">
          <template v-if="isOdoo">
            <a href="#" :class="$style.a">
              <KParentTooltip>Open in Odoo</KParentTooltip>
              Available
              <v-icon :class="$style.icon" icon="mdi-open-in-new"></v-icon>
            </a>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Invoice status">
          <template v-if="job.invoice_status">
            {{ job.invoice_status }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="Payment status">
          <template v-if="job.payment_status">
            {{ job.payment_status }}
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
      <div v-if="props.isStaff" :class="$style.itemWrapper">
        <DetailedJobInfoDescListItem :class="$style.item" term="On board">
          <template
            v-if="job.schedule_board?.name && props.job?.rockhopper_date"
          >
            <router-link
              :class="$style.a"
              :to="{
                name: routeNames.SCHEDULER_BOARD,
                params: {
                  division: job.schedule_board.name.split('/')[0].toLowerCase(),
                  date: props.job.rockhopper_date, // TODO props.job vs computed job Error
                },
              }"
              target="_blank"
            >
              <v-icon :class="$style.icon" icon="mdi-open-in-new"></v-icon>
              {{ job.schedule_board.name }}
            </router-link>
          </template>
          <template v-else>{{ commonConstants.EM_DASH }}</template>
        </DetailedJobInfoDescListItem>
      </div>
    </div>
  </dl>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobAdditionalInfoDescList {
  line-height: 1.4;
  font-size: 16px;
  margin: 0;
}
  .wrapper {
    margin-bottom: -16px;
    columns: 3 150px;
    column-gap: 20px;
  }
    .itemWrapper {
    }
      .item {
        display: inline-block;
        margin: 0 0 16px 0;
      }
        .a {
          text-decoration: none;
        }
          .icon {
            font-size: 17px;
            margin-top: -1px;
          }
</style>
